import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Flower2, BookOpen, Flame, Sparkles, ArrowRight, MoveHorizontal, ChevronDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { links } from "@/config/links";
import { trackEvent } from "@/api/client";
import { ScratchCard } from "@/components/ScratchCard";
import { RevealBurst } from "@/components/RevealBurst";
import { JourneyBlock } from "@/components/journey/JourneyBlock";
import { JourneyTabs } from "@/components/journey/JourneyTabs";
import { MobileInterestBar } from "@/components/journey/InterestCTA";
import {
  DEFAULT_JOURNEYS,
  DEFAULT_GIRI_WHATSAPP,
  DEFAULT_BOOK_URL,
  DEFAULT_SPONSOR,
  PATH_TAGS,
  waLink,
} from "@/config/journeys";

const CARDS = [
  { key: "bhagavatam", itemIndex: 3, img: "/images/cards/bhagavatam.jpg", bottomIcon: BookOpen, primary: true },
  { key: "mahaprasadam", itemIndex: 0, img: "/images/cards/mahaprasadam.jpg", bottomIcon: Flower2 },
  { key: "chanting", itemIndex: 1, img: "/images/cards/mala.jpg", bottomIcon: Sparkles },
  { key: "darshan", itemIndex: 2, img: "/images/cards/darshan.jpg", bottomIcon: Flame },
];

const ICONS = { "book-open": BookOpen, "flower-2": Flower2, sparkles: Sparkles, flame: Flame };
const IMG_FALLBACK = {
  bhagavatam: "/images/cards/bhagavatam.jpg",
  mahaprasadam: "/images/cards/mahaprasadam.jpg",
  chanting: "/images/cards/mala.jpg",
  darshan: "/images/cards/darshan.jpg",
  default: "/images/cards/bhagavatam.jpg",
};

const cardShape = "rounded-t-[64px] rounded-b-[22px]";

export const FourPaths = ({ campaign = null, onJourneyState }) => {
  const { t, lang } = useLanguage();
  const p = t.paths;
  const [burst, setBurst] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [activePath, setActivePath] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const indicatorRef = useRef(null);
  const journeyRef = useRef(null);
  const isPlaceholder = (href) => !href || href === "#";

  const journeys = campaign?.journeys || DEFAULT_JOURNEYS;
  const giriWhatsapp = campaign?.giri_whatsapp || DEFAULT_GIRI_WHATSAPP;
  const bookUrl = campaign?.book_url || DEFAULT_BOOK_URL;
  const sponsor = campaign?.sponsor || DEFAULT_SPONSOR;
  const journeyFor = (key) => journeys[key] || DEFAULT_JOURNEYS[key] || null;

  // Editable "sacred ways" heading/sub (campaign override, else translation default).
  const pick = (en, hi) => (lang === "hi" ? hi || en : en || hi);
  const headingText =
    pick(campaign?.paths_heading_en, campaign?.paths_heading_hi) || p.heading;
  const subText = pick(campaign?.paths_sub_en, campaign?.paths_sub_hi) || p.sub;

  const cards = (
    campaign && Array.isArray(campaign.ctas) && campaign.ctas.length
      ? campaign.ctas
          .filter((c) => c.visible !== false)
          .map((c) => ({
          key: c.key || c.id,
          img: c.image || IMG_FALLBACK[c.key] || IMG_FALLBACK.default,
          BottomIcon: ICONS[c.icon] || Sparkles,
          primary: !!c.primary,
          title: lang === "hi" ? c.title_hi || c.title_en : c.title_en,
          desc: lang === "hi" ? c.desc_hi || c.desc_en : c.desc_en,
          cta: lang === "hi" ? c.label_hi || c.label_en : c.label_en,
          href: c.url || "#",
        }))
      : CARDS.map((card) => {
          const item = p.items[card.itemIndex];
          return {
            key: card.key,
            img: card.img,
            BottomIcon: card.bottomIcon,
            primary: !!card.primary,
            title: item.title,
            desc: item.desc,
            cta: item.cta,
            href: links[card.key] || "#",
          };
        })
  );

  // Adaptive layout: with 1–2 cards we drop the swipe carousel for a centred
  // grid; on desktop each card becomes a wide horizontal (image left / text right).
  const few = cards.length <= 2;

  // Cards that have a journey attached become tabs / interest options.
  const journeyCards = cards.filter((c) => journeyFor(c.key));
  const options = journeyCards.map((card) => {
    const j = journeyFor(card.key);
    const conv = (j.modules || []).find((m) => m.type === "conversion");
    const tag = `Interested - ${PATH_TAGS[card.key] || card.title}${
      campaign?.slug ? ` · ${campaign.slug}` : ""
    }`;
    return {
      key: card.key,
      label: PATH_TAGS[card.key] || card.title,
      revealed: true,
      href: waLink(giriWhatsapp, conv?.wa_message, "", tag),
    };
  });
  const activeOption = options.find((o) => o.key === activePath) || null;
  const anyRevealed = unlocked;
  const firstPathKey = journeyCards[0]?.key || null;

  // Report state up so Landing can render the header tabs + "I'm Interested" dropdown.
  useEffect(() => {
    onJourneyState?.({ anyRevealed, options, active: activePath, setActive: setActivePath });
  }, [unlocked, activePath, lang]);

  const handleCtaClick = (e, href) => {
    if (isPlaceholder(href)) {
      e.preventDefault();
      return;
    }
    if (campaign?.slug) trackEvent(campaign.slug, "cta_click");
  };

  const handleReveal = (card) => {
    setRevealed((r) => (r[card.key] ? r : { ...r, [card.key]: true }));
    // Always start on the first tab, no matter which card was scratched.
    if (!unlocked) {
      setUnlocked(true);
      setActivePath(firstPathKey);
    }
    if (card.primary) {
      setBurst(true);
      if (campaign?.slug) trackEvent(campaign.slug, "journey_completed");
    }
    // Gentle nudge so the scroll indicator peeks in — never a jump to content.
    setTimeout(() => indicatorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 220);
  };

  const activeJourney = activePath ? journeyFor(activePath) : null;
  const scrollToJourney = () =>
    journeyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section id="paths" data-testid="four-paths-section" className="relative scroll-mt-16 pt-20 pb-16 md:pt-24">
      <div className="mx-auto max-w-6xl px-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7 }}
          className="mb-8 text-center"
        >
          <h2 id="paths-title" className="scroll-mt-20 font-display text-3xl leading-tight text-white sm:text-5xl md:text-6xl">
            {headingText}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base text-amber-50/60">{subText}</p>
          <p className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-300/70 sm:hidden">
            <MoveHorizontal className="h-3.5 w-3.5" />{" "}
            {lang === "hi" ? "स्वाइप करें और प्रत्येक कार्ड खुरचें" : "Swipe & scratch each card"}
          </p>
        </motion.div>
      </div>

      {/* carousel (3+ cards) or centred grid (1–2 cards) */}
      <div
        data-testid="paths-carousel"
        className={
          few
            ? "mx-auto flex max-w-4xl flex-col items-center justify-center gap-6 px-6 pb-6 sm:flex-row sm:flex-wrap sm:items-stretch"
            : "flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-pl-6 px-6 pb-6 [-ms-overflow-style:none] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-amber-300/30 [&::-webkit-scrollbar]:h-1.5"
        }
      >
        {!few && (
          <div className="shrink-0 lg:w-[calc((100vw-72rem)/2-1.5rem)]" aria-hidden />
        )}

        {cards.map((card, pos) => {
          const href = card.href;
          const primary = card.primary;
          const isRevealed = !!revealed[card.key];
          return (
            <motion.div
              key={card.key}
              animate={{ scale: isRevealed ? 1.02 : 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className={
                few
                  ? "w-[86vw] max-w-[300px] shrink-0 sm:w-[440px] sm:max-w-[460px] lg:w-[500px] lg:max-w-[520px]"
                  : "w-[76vw] max-w-[300px] shrink-0 snap-center sm:w-[300px]"
              }
            >
              <ScratchCard
                testId={`path-card-${pos}`}
                hint={p.scratchHint}
                className={cardShape}
                onReveal={() => handleReveal(card)}
              >
                <div
                  className={`relative flex h-full ${cardShape} border px-5 pb-6 pt-7 ${
                    few
                      ? "flex-col items-center text-center sm:flex-row sm:items-center sm:gap-5 sm:text-left"
                      : "flex-col items-center text-center"
                  } ${
                    primary
                      ? "border-amber-300 bg-gradient-to-b from-[#20280f] via-[#141a2e] to-[#0a0f24] shadow-[0_0_70px_rgba(251,191,36,0.42)]"
                      : "border-amber-200/20 bg-gradient-to-b from-[#141b3a] to-[#0a0f24]"
                  }`}
                >
                  {primary && <RevealBurst show={burst} />}

                  <div
                    className={`absolute -top-2 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 ${
                      primary ? "bg-amber-300" : "bg-amber-200/40"
                    }`}
                  />

                  {/* image */}
                  <div
                    className={`relative overflow-hidden rounded-2xl ${
                      few
                        ? "mb-4 aspect-square w-full sm:mb-0 sm:aspect-[4/5] sm:w-1/2 sm:shrink-0"
                        : "mb-4 aspect-square w-full"
                    }`}
                  >
                    {primary && (
                      <Crown className="absolute left-1/2 top-2 z-10 h-6 w-6 -translate-x-1/2 text-amber-300 drop-shadow-[0_0_10px_rgba(251,191,36,0.7)]" />
                    )}
                    <img
                      src={card.img}
                      alt={card.title}
                      loading="lazy"
                      className="h-full w-full object-cover"
                      data-testid={`path-image-${pos}`}
                    />
                  </div>

                  {/* content */}
                  <div
                    className={`flex flex-1 flex-col ${
                      few
                        ? "items-center sm:items-start"
                        : "w-full items-center"
                    }`}
                  >
                    <div
                      className={`flex h-9 w-9 items-center justify-center rounded-full border font-display text-sm ${
                        few ? "mb-3" : "mb-3"
                      } ${
                        primary
                          ? "border-amber-300 bg-amber-400/20 text-amber-200 shadow-[0_0_20px_rgba(251,191,36,0.4)]"
                          : "border-amber-200/40 bg-white/5 text-amber-100/80"
                      }`}
                    >
                      {pos + 1}
                    </div>

                    <h3
                      className={`font-display leading-tight ${
                        primary ? "text-2xl text-amber-300" : "text-xl text-amber-200"
                      }`}
                      data-testid={`path-title-${pos}`}
                    >
                      {card.title}
                    </h3>

                    <p
                      className={`mt-2 flex-1 text-sm leading-relaxed text-amber-50/60 ${
                        few ? "" : "line-clamp-2"
                      }`}
                    >
                      {card.desc}
                    </p>

                    <a
                      href={href}
                      onClick={(e) => handleCtaClick(e, href)}
                      target={!isPlaceholder(href) ? "_blank" : undefined}
                      rel={!isPlaceholder(href) ? "noopener noreferrer" : undefined}
                      data-testid={`path-link-${pos}`}
                      className={`group mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-all ${
                        primary
                          ? "bg-amber-400 text-[#0a0f24] hover:bg-amber-300"
                          : "border border-amber-300/60 text-amber-200 hover:border-amber-300 hover:bg-amber-400/10"
                      }`}
                    >
                      <span className="truncate">{card.cta}</span>
                      <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              </ScratchCard>
            </motion.div>
          );
        })}
        {!few && <div className="shrink-0 w-2 sm:w-6" aria-hidden />}
      </div>

      <div className="mx-auto max-w-3xl px-5">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-10 text-center"
        >
          <p className="text-sm text-amber-50/55">✦ {p.footer}</p>
          <p className="mt-2 font-display text-xl text-amber-300">{p.footerAccent}</p>
        </motion.div>
      </div>

      {/* After the first scratch: unlock all journeys, show a scroll indicator
          (no jump to content), and offer tabs to switch between paths. */}
      {unlocked && (
        <div ref={indicatorRef} className="mt-10 flex justify-center">
          <button
            type="button"
            data-testid="journey-scroll-indicator"
            onClick={scrollToJourney}
            className="flex flex-col items-center gap-2 text-amber-300/80 transition-colors hover:text-amber-200"
          >
            <span className="text-xs uppercase tracking-[0.3em]">
              {activeJourney?.nudge || "Scroll to explore"}
            </span>
            <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
              <ChevronDown className="h-6 w-6" />
            </motion.span>
          </button>
        </div>
      )}

      {unlocked && activeJourney && (
        <div ref={journeyRef} className="mt-8 scroll-mt-16">
          {/* Mobile tab bar (desktop tabs live in the header) */}
          <JourneyTabs options={options} active={activePath} onSelect={setActivePath} variant="bar" className="sm:hidden" />
          <JourneyBlock
            key={activePath}
            pathKey={activePath}
            journey={activeJourney}
            giriWhatsapp={giriWhatsapp}
            bookUrl={bookUrl}
            slug={campaign?.slug}
            sponsor={sponsor}
            lang={lang}
          />
        </div>
      )}

      <MobileInterestBar option={unlocked ? activeOption : null} />
    </section>
  );
};
